import { useState } from 'react'
import { X, Plus } from 'lucide-react'
import { useCascadeStore } from '../store'
import {
  type CascadeNode, type CaptureOwner, type DobFormat, type AddressMethod, type NationalIdType, type TaxIdType,
} from '../types'

// Vendor options per capture method type
const DOC_CAPTURE_VENDORS = ['Onfido', 'Persona', 'Jumio', 'Sumsub', 'Other']
const SELFIE_CAPTURE_VENDORS = ['Daon', 'Onfido', 'Sumsub', 'Persona', 'Other']
const FORM_CAPTURE_VENDORS = ['Onfido', 'Persona', 'Other']

/**
 * Detail editor panel for capture-method nodes.
 * Shows client/vendor toggle, collected fields with optional/required,
 * field-specific config (DOB format, address method), and notes.
 */
export function NodeDetailPanel({ node }: { node: CascadeNode }) {
  const {
    toggleCollectedField, setCollectedFieldOptional,
    setCollectedFieldDobFormat, setCollectedFieldAddressMethod,
    setCollectedFieldPossessionVerified,
    setCollectedFieldNationalIdType, setCollectedFieldNationalIdOther,
    setCollectedFieldTaxIdType, setCollectedFieldTaxIdOther,
    addCustomCollectedField, removeCustomCollectedField,
    setNodeNotes, setCaptureOwner, setCaptureVendorName,
  } = useCascadeStore()

  const [customFieldName, setCustomFieldName] = useState('')

  const enabledCount = node.collectedFields?.filter(f => f.enabled).length ?? 0
  const owner = node.captureOwner ?? 'client'

  return (
    <div className="space-y-4">
      {/* Client vs Vendor header toggle */}
      <div className="rounded-xl overflow-hidden border border-[var(--border-color)]">
        <div className="flex">
          {([['client', 'Client', '🖥️'], ['vendor', 'Vendor', '🏢']] as [CaptureOwner, string, string][]).map(([value, label, icon]) => (
            <button
              key={value}
              onClick={() => setCaptureOwner(node.id, value)}
              className={`flex-1 flex items-center justify-center gap-1 py-2.5 text-[11px] font-semibold transition-colors ${
                owner === value
                  ? 'bg-violet-600 text-white'
                  : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]'
              }`}
            >
              <span>{icon}</span> {label}
            </button>
          ))}
        </div>
        <div className="px-3 py-2 bg-[var(--bg-tertiary)]">
          <p className="text-[9px] text-[var(--text-muted)] leading-relaxed">
            {owner === 'client'
              ? "Data is captured on the client's own app or website."
              : "A vendor's forms are exposed to the end user to capture their details."}
          </p>
          {owner === 'vendor' && (() => {
            const vendorOptions = node.captureMethodType === 'selfie' ? SELFIE_CAPTURE_VENDORS
              : node.captureMethodType === 'id-verification' ? DOC_CAPTURE_VENDORS
              : FORM_CAPTURE_VENDORS
            const currentVendor = node.captureVendorName ?? ''
            const isOther = currentVendor && !vendorOptions.includes(currentVendor) && currentVendor !== 'Other'
            const selectValue = isOther ? 'Other' : currentVendor
            return (
              <div className="mt-2 space-y-1.5">
                <select
                  value={selectValue}
                  onChange={(e) => {
                    const val = e.target.value
                    setCaptureVendorName(node.id, val === 'Other' ? '' : val)
                  }}
                  className="w-full text-[11px] px-2.5 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Select vendor...</option>
                  {vendorOptions.map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
                {(selectValue === 'Other' || isOther) && (
                  <input
                    type="text"
                    value={isOther ? currentVendor : ''}
                    onChange={(e) => setCaptureVendorName(node.id, e.target.value)}
                    placeholder="Enter vendor name..."
                    className="w-full text-[11px] px-2.5 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                )}
              </div>
            )
          })()}
        </div>
      </div>

      {/* Field toggles */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">
            Fields Collected
          </span>
          <span className="text-[10px] text-[var(--text-muted)]">
            {enabledCount} selected
          </span>
        </div>

        <div className="space-y-1">
          {node.collectedFields?.map(field => (
            <div
              key={field.id}
              className={`rounded-lg border transition-all ${
                field.enabled
                  ? 'border-blue-500/30 bg-blue-500/5'
                  : 'border-transparent bg-[var(--bg-tertiary)] opacity-50 hover:opacity-75'
              }`}
            >
              <div className="flex items-center gap-2 px-2.5 py-1.5">
                {/* Toggle switch */}
                <button
                  onClick={() => toggleCollectedField(node.id, field.id)}
                  className={`shrink-0 w-7 h-4 rounded-full transition-colors relative ${
                    field.enabled ? 'bg-blue-500' : 'bg-gray-600'
                  }`}
                >
                  <div
                    className="absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform"
                    style={{ left: 2, transform: field.enabled ? 'translateX(12px)' : 'translateX(0)' }}
                  />
                </button>

                {/* Label */}
                <span className={`text-[11px] font-medium flex-1 ${
                  field.enabled ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'
                }`}>
                  {field.label}
                </span>

                {/* Optional / Required toggle (only when enabled) */}
                {field.enabled && (
                  <button
                    onClick={() => setCollectedFieldOptional(node.id, field.id, !field.optional)}
                    className={`text-[8px] px-1.5 py-0.5 rounded font-medium transition-colors ${
                      field.optional
                        ? 'bg-gray-500/15 text-[var(--text-muted)]'
                        : 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
                    }`}
                  >
                    {field.optional ? 'Optional' : 'Required'}
                  </button>
                )}
              </div>

              {/* DOB format selector */}
              {field.enabled && field.id === 'dob' && (
                <div className="px-2.5 pb-2 flex gap-1">
                  {([['full', 'Full Date'], ['year-only', 'Year Only']] as [DobFormat, string][]).map(([value, label]) => (
                    <button
                      key={value}
                      onClick={() => setCollectedFieldDobFormat(node.id, field.id, value)}
                      className={`text-[8px] px-2 py-1 rounded transition-colors ${
                        (field.dobFormat ?? 'full') === value
                          ? 'bg-blue-600 text-white'
                          : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {/* Address method selector */}
              {field.enabled && field.id === 'address' && (
                <div className="px-2.5 pb-2 flex gap-1">
                  {([['autofill', 'Autofill'], ['broken-down', 'Broken Down'], ['open-field', 'Open Field']] as [AddressMethod, string][]).map(([value, label]) => (
                    <button
                      key={value}
                      onClick={() => setCollectedFieldAddressMethod(node.id, field.id, value)}
                      className={`text-[8px] px-2 py-1 rounded transition-colors ${
                        (field.addressMethod ?? 'autofill') === value
                          ? 'bg-blue-600 text-white'
                          : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {/* Possession verified toggle (email & phone) */}
              {field.enabled && (field.id === 'email' || field.id === 'phone') && (
                <div className="px-2.5 pb-2 flex items-center gap-2">
                  <button
                    onClick={() => setCollectedFieldPossessionVerified(node.id, field.id, !field.possessionVerified)}
                    className={`shrink-0 w-7 h-4 rounded-full transition-colors relative ${
                      field.possessionVerified ? 'bg-emerald-500' : 'bg-gray-600'
                    }`}
                  >
                    <div
                      className="absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform"
                      style={{ left: 2, transform: field.possessionVerified ? 'translateX(12px)' : 'translateX(0)' }}
                    />
                  </button>
                  <span className="text-[9px] text-[var(--text-muted)]">
                    OTP / link sent to verify possession
                  </span>
                </div>
              )}

              {/* National ID type selector */}
              {field.enabled && field.id === 'national_id' && (
                <div className="px-2.5 pb-2 space-y-1.5">
                  <div className="flex gap-1">
                    {([['passport', 'Passport'], ['drivers-licence', "Driver's Licence"], ['other', 'Other']] as [NationalIdType, string][]).map(([value, label]) => (
                      <button
                        key={value}
                        onClick={() => setCollectedFieldNationalIdType(node.id, field.id, value)}
                        className={`text-[8px] px-2 py-1 rounded transition-colors ${
                          (field.nationalIdType ?? 'passport') === value
                            ? 'bg-blue-600 text-white'
                            : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {field.nationalIdType === 'other' && (
                    <input
                      type="text"
                      value={field.nationalIdOther ?? ''}
                      onChange={(e) => setCollectedFieldNationalIdOther(node.id, field.id, e.target.value)}
                      placeholder="e.g. Medicare card, Tax File Number..."
                      className="w-full text-[10px] px-2 py-1.5 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  )}
                </div>
              )}

              {/* Tax ID type selector */}
              {field.enabled && field.id === 'tax_id' && (
                <div className="px-2.5 pb-2 space-y-1.5">
                  <div className="flex gap-1">
                    {([['ssn', 'SSN'], ['other', 'Other']] as [TaxIdType, string][]).map(([value, label]) => (
                      <button
                        key={value}
                        onClick={() => setCollectedFieldTaxIdType(node.id, field.id, value)}
                        className={`text-[8px] px-2 py-1 rounded transition-colors ${
                          (field.taxIdType ?? 'ssn') === value
                            ? 'bg-blue-600 text-white'
                            : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {field.taxIdType === 'other' && (
                    <input
                      type="text"
                      value={field.taxIdOther ?? ''}
                      onChange={(e) => setCollectedFieldTaxIdOther(node.id, field.id, e.target.value)}
                      placeholder="e.g. TFN, ITIN, EIN..."
                      className="w-full text-[10px] px-2 py-1.5 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  )}
                </div>
              )}

              {/* Remove button for custom fields */}
              {field.isCustom && (
                <div className="px-2.5 pb-2">
                  <button
                    onClick={() => removeCustomCollectedField(node.id, field.id)}
                    className="flex items-center gap-1 text-[8px] text-red-400 hover:text-red-300 transition-colors"
                  >
                    <X size={8} /> Remove field
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add custom field */}
        <div className="flex gap-1 mt-2">
          <input
            type="text"
            value={customFieldName}
            onChange={(e) => setCustomFieldName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && customFieldName.trim()) {
                addCustomCollectedField(node.id, customFieldName.trim())
                setCustomFieldName('')
              }
            }}
            placeholder="Add custom field..."
            className="flex-1 text-[10px] px-2 py-1.5 rounded-lg bg-[var(--bg-tertiary)] border border-dashed border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button
            onClick={() => {
              if (customFieldName.trim()) {
                addCustomCollectedField(node.id, customFieldName.trim())
                setCustomFieldName('')
              }
            }}
            className="text-[10px] px-2 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-colors"
          >
            <Plus size={10} />
          </button>
        </div>
      </div>

      {/* Notes */}
      <div>
        <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-1.5 block">
          Notes
        </span>
        <textarea
          value={node.nodeNotes ?? ''}
          onChange={(e) => setNodeNotes(node.id, e.target.value)}
          placeholder="e.g. Address uses Google Places autocomplete, name fields are free-text..."
          className="w-full text-[11px] p-2.5 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none leading-relaxed"
          rows={3}
        />
      </div>
    </div>
  )
}
